import { Link } from 'react-router-dom';
import { Phone, Mail, Shield, Award, MapPin } from 'lucide-react';
import { companyInfo } from '../data/pricing';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-gray-300 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_landlord-safety-1/artifacts/ez010v5h_crescent.JPG"
                alt="Crescent Electrical Ltd"
                className="h-12 w-auto"
              />
              <span className="text-white font-bold text-xl">Crescent Electrical</span>
            </div>
            <p className="text-sm text-gray-400 mb-6">
              Professional electrical contractor for rewires, extensions, renovations and new builds across London.
            </p>
            <div className="flex items-center gap-4 mb-4">
              <Shield className="h-5 w-5 text-[#2563EB]" />
              <span className="text-sm text-gray-300">NICEIC Approved</span>
            </div>
            <div className="flex items-center gap-4">
              <Award className="h-5 w-5 text-[#F97316]" />
              <span className="text-sm text-gray-300">Fully Insured</span>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold mb-6">Our Services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/rewiring" className="text-sm text-gray-400 hover:text-[#F97316] transition-colors">
                  House & Flat Rewiring
                </Link>
              </li>
              <li>
                <Link to="/extensions" className="text-sm text-gray-400 hover:text-[#F97316] transition-colors">
                  Extensions & Renovations
                </Link>
              </li>
              <li>
                <Link to="/new-build-electrician" className="text-sm text-gray-400 hover:text-[#F97316] transition-colors">
                  New Build Electrical
                </Link>
              </li>
              <li>
                <Link to="/kitchen-electrician" className="text-sm text-gray-400 hover:text-[#F97316] transition-colors">
                  Kitchen Electrics
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-sm text-gray-400 hover:text-[#F97316] transition-colors">
                  Consumer Units
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-sm text-gray-400 hover:text-[#F97316] transition-colors">
                  EICRs & Testing
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-bold mb-6">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-sm text-gray-400 hover:text-[#0066FF] transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/projects" className="text-sm text-gray-400 hover:text-[#0066FF] transition-colors">
                  Our Projects
                </Link>
              </li>
              <li>
                <Link to="/reviews" className="text-sm text-gray-400 hover:text-[#0066FF] transition-colors">
                  Customer Reviews
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-sm text-gray-400 hover:text-[#0066FF] transition-colors">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-gray-400 hover:text-[#0066FF] transition-colors">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold mb-6">Get In Touch</h4>
            <div className="space-y-4">
              <a 
                href={`tel:${companyInfo.phone}`}
                className="flex items-start gap-3 text-sm text-gray-400 hover:text-[#0066FF] transition-colors group"
              >
                <Phone className="h-5 w-5 flex-shrink-0 mt-0.5 group-hover:text-[#0066FF]" />
                <div>
                  <div className="text-white font-semibold mb-1">Call Us</div>
                  <div>{companyInfo.phone}</div>
                </div>
              </a>
              
              <a 
                href={`mailto:${companyInfo.email}`}
                className="flex items-start gap-3 text-sm text-gray-400 hover:text-[#0066FF] transition-colors group"
              >
                <Mail className="h-5 w-5 flex-shrink-0 mt-0.5 group-hover:text-[#0066FF]" />
                <div>
                  <div className="text-white font-semibold mb-1">Email Us</div>
                  <div className="break-all">{companyInfo.email}</div>
                </div>
              </a>

              <div className="flex items-start gap-3 text-sm text-gray-400">
                <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-white font-semibold mb-1">Service Area</div>
                  <div>Greater London</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="text-center mb-6">
            <p className="text-sm text-gray-400 mb-2">
              <strong className="text-gray-300">Crescent Electrical Ltd</strong> | Company No. 10335734
            </p>
            <p className="text-xs text-gray-500">
              Registered office: 67a Station Road, London, England, E4 7BU | Registered in England and Wales
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-4 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © {currentYear} Crescent Electrical Ltd. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-[#2563EB] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookies" className="text-sm text-gray-400 hover:text-[#2563EB] transition-colors">
                Cookie Policy
              </Link>
              <Link to="/terms" className="text-sm text-gray-400 hover:text-[#2563EB] transition-colors">
                Terms of Use
              </Link>
              <button 
                onClick={() => {
                  localStorage.removeItem('cookie_consent');
                  window.location.reload();
                }}
                className="text-sm text-gray-400 hover:text-[#2563EB] transition-colors"
              >
                Cookie Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
